import {create} from 'javascript-state-machine';

import {Event, JSM_INITIAL_EVENT} from '../enums/Event';
import {createVideoStartFailedReason} from '../enums/VideoStartFailedReason';
import {AnalyticsEventBase} from '../types/EventData';
import {logger} from '../utils/Logger';
import {isFunction} from '../utils/Utils';

import {AnalyticsStateMachine} from './AnalyticsStateMachine';
import {
  customStateMachineErrorCallback,
  getAllStatesBut,
  logMissingCallbackWarning,
  logStateMachineTransition,
  whileIn,
} from './stateMachineUtils';

export enum State {
  /**
   * We track and consider the buffering state only when it comes from the {@link State.PLAYING},
   * aka. when the video was playing and stopped because it started to buffer.
   */
  BUFFERING = 'BUFFERING',
  /**
   * This state was created only to be able to "cut" the {@link State.PLAYING} and {@link State.PAUSE}
   * and send playing/pause sample with current custom data, while calling {@link AdapterAPI.setCustomData} function
   * and setting the new custom data values.
   *
   * @remarks
   *
   * Our implementation relays on a specific state name "CUSTOMDATACHANGE" because it should call the
   * default "onleavestate" state machine callback {@link stateMachineCallbacks.customdatachange}.
   */
  CUSTOMDATACHANGE = 'CUSTOMDATACHANGE',
  ERROR = 'ERROR',
  PAUSE = 'PAUSE',
  PLAYING = 'PLAYING',
  READY = 'READY',
  /**
   * We track and consider the "seeking" state only when it comes from the {@link State.PLAYING},
   * aka. when the video has already started to play and is already playing.
   */
  SEEKING = 'SEEKING',
  /**
   * We do not track the "seeking" while the playback is paused!
   *
   * We should not produce a "seeking" sample, but rather we should just extend the existing "pause" sample.
   *
   * Why do we recognize this state?
   *  - because during the seeking, {@link Event.TIMECHANGED} is fired and if we would just use {@link State.PAUSE},
   *    it would trigger a JSM transition to {@link State.PLAYING} and break the tracking of current pause.
   *  - because once seeking is finished, we need to transition the JSM back to {@link State.PAUSE}
   *
   *  @remarks
   *
   *  To be sure state machine works correctly, we need to be sure only calling {@link Event.SEEK} from
   *  {@link State.PAUSE} should transition JSM to {@link State.SEEKING_IN_PAUSE}!
   */
  SEEKING_IN_PAUSE = 'SEEKING_IN_PAUSE',
  SETUP = 'SETUP',
  SOURCE_CHANGING = 'SOURCE_CHANGING',
  STARTUP = 'STARTUP',
  /**
   * The browser window was closed (window 'unload' event), aka. sender has stopped the casting
   */
  UNLOADED = 'UNLOADED',
  VIDEOSTART_FAILED = 'VIDEOSTART_FAILED',
}

export class CAFv3AnalyticsStateMachine extends AnalyticsStateMachine {
  override createStateMachine(): StateMachine.StateMachine {
    return create({
      // custom initial state and custom JSM initial event name
      // initial event is used by JSM for the initial state transition 'none' -> 'SETUP'
      initial: {state: State.SETUP, event: JSM_INITIAL_EVENT},
      error: customStateMachineErrorCallback,
      events: [
        ...whileIn(State.SETUP).passOver(
          // We track and consider the buffering state only when it comes from the State.PLAYING
          Event.START_BUFFERING,
        ),

        {name: Event.SOURCE_LOADED, from: State.SETUP, to: State.READY},
        {name: Event.READY, from: State.SETUP, to: State.READY},

        ...whileIn(State.READY).passOver(
          Event.READY,
          // We track and consider the buffering state only when it comes from the State.PLAYING
          Event.START_BUFFERING,
          Event.END_BUFFERING,
          // this will fire whenever the currentTime attribute changes
          Event.TIMECHANGED,
          Event.SEEK,
          Event.SEEKED,
        ),

        {name: Event.PLAY, from: State.READY, to: State.STARTUP},

        {name: Event.ERROR, from: State.STARTUP, to: State.VIDEOSTART_FAILED},
        {name: Event.UNLOAD, from: State.STARTUP, to: State.VIDEOSTART_FAILED},
        {name: Event.VIDEOSTART_TIMEOUT, from: State.STARTUP, to: State.VIDEOSTART_FAILED},

        {name: Event.ERROR, from: getAllStatesBut([State.STARTUP], State), to: State.ERROR},
        {name: Event.UNLOAD, from: getAllStatesBut([State.STARTUP], State), to: State.UNLOADED},

        // We need to wait for the 'TIME_UPDATE' event to also include startup video buffering in State.STARTUP time.
        //
        // We shouldn't use 'PLAYING' event, because that event is fired immediately, followed by buffering events that
        // would cut State.STARTUP sooner, then move us to State.PLAYING and then even to State.BUFFERING.
        {name: Event.TIMECHANGED, from: State.STARTUP, to: State.PLAYING},
        ...whileIn(State.PLAYING).passOver(Event.TIMECHANGED),

        {name: Event.START_BUFFERING, from: State.PLAYING, to: State.BUFFERING},
        // While in State.BUFFERING, we are waiting for 'TIME_UPDATE' event to be able to recognize end of the
        // buffering and to return back to State.PLAYING. The relay also on fact that State.BUFFERING can only
        // start from State.PLAYING.
        //
        // We tried to use the 'BUFFERING' event containing `isBuffering: false`, but that was not reliable,
        // because of a scenario when the 'error' event would happen while buffering (because of buffering timeout
        // or Shaka player throwing error). Because this 'BUFFERING' event is also fired when player throws error, and
        // it would transition us to State.PLAYING and then it would send the short "playing" sample, before 'ERROR'
        // event would be fired and would move us to State.ERROR.
        {name: Event.TIMECHANGED, from: State.BUFFERING, to: State.PLAYING},

        {name: Event.PAUSE, from: State.PLAYING, to: State.PAUSE},
        {name: Event.PAUSE, from: State.BUFFERING, to: State.PAUSE},
        // if a 'PAUSE' event happens during State.SEEKING, we need to transition to State.PAUSE
        // instead of recovering back State.PLAYING (where State.SEEKING was started from)
        {name: Event.PAUSE, from: State.SEEKING, to: State.PAUSE},
        ...whileIn(State.PAUSE).passOver(
          // We track and consider the buffering state only when it comes from the State.PLAYING
          Event.START_BUFFERING,
          Event.END_BUFFERING,
        ),
        // We use Event.PLAYING because it is more reliable for tracking transition from State.PAUSE to State.PLAYING.
        //
        // We shouldn't use 'TIME_UPDATE' to interrupt State.PAUSE, because that event is fired when the time indicated
        // by the currentTime property has been updated.
        //    Example: this happens when a user clicks 'PAUSE' during the seeking. Our state machine should move to
        //    State.PAUSE asap, but later because of the seeking, the events 'SEEKED' and 'TIME_UPDATE' are still fired,
        //    and that would break our state machine as we would end up in State.PLAYING
        {name: Event.PLAYING, from: State.PAUSE, to: State.PLAYING},

        {name: Event.SEEK, from: State.PAUSE, to: State.SEEKING_IN_PAUSE},
        ...whileIn(State.SEEKING_IN_PAUSE).passOver(
          Event.SEEK,
          // We track and consider the buffering state only when it comes from the State.PLAYING
          Event.START_BUFFERING,
          Event.END_BUFFERING,
          // this will fire whenever the currentTime attribute changes
          Event.TIMECHANGED,
        ),
        {name: Event.SEEKED, from: State.SEEKING_IN_PAUSE, to: State.PAUSE},

        {name: Event.SEEK, from: State.PLAYING, to: State.SEEKING},
        ...whileIn(State.SEEKING).passOver(
          Event.SEEK,
          // We track and consider the buffering state only when it comes from the State.PLAYING
          Event.START_BUFFERING,
          Event.END_BUFFERING,
          // this will fire whenever the currentTime attribute changes
          Event.TIMECHANGED,
        ),
        {name: Event.SEEKED, from: State.SEEKING, to: State.PLAYING},

        {
          name: Event.MANUAL_SOURCE_CHANGE,
          // We dont track source change during SETUP
          from: getAllStatesBut([State.SETUP], State),
          to: State.SOURCE_CHANGING,
        },
        // Case: ADAPTER API SOURCE CHANGE VS. PLAYER SOURCE CHANGE
        // Problem:
        //    The Event.MANUAL_SOURCE_CHANGE is fired in two cases:
        //    1) when the player source change is detected by InternalAdapter
        //    2) when the AdapterAPI.changeSource() is called by the customer
        //    This means that we can have two consecutive Event.MANUAL_SOURCE_CHANGE events in possible two orders.
        //
        //    AdapterAPI.changeSource() call is optional and should be called before the attempt for player source change!!
        //    It supposes to let us know that the player source change will be triggered, in case when we can't automatically
        //    detect it from the player events (e.g., HTML native adapter) and mostly to be able to pass the new
        //    AnalyticsConfig for the new source.
        //
        //    But because its call is implemented by the customer, we can't be sure about the order of these two events.
        // Solution:
        //    1) We want to mostly support the order `(optional) AdapterAPI.changeSource() -> player source change`.
        //    This should be communicated to the customer as the recommended way because it is more intuitive to call
        //    AdapterAPI.changeSource() before doing the player source change.
        //
        //    2) We also want to support "partially" the opposite order `player source change -> AdapterAPI.changeSource()`,
        //    BUT only in the case when the AdapterAPI.changeSource() is called while we are already in State.SOURCE_CHANGING,
        //    which means that the player source change has already started, and we are waiting for the source to be loaded.
        //    Why? Because in some customer integrations, the customer can't call AdapterAPI.changeSource() "proactively"
        //    (e.g., platforms like React Native, Chromecast) but rather "reactively" in response to the player event that
        //    we also relay on to detect the player source change. That means that the customer player event handler
        //    is called always after ours.
        ...whileIn(State.SOURCE_CHANGING).passOver(Event.MANUAL_SOURCE_CHANGE),
        // just pass over events to avoid console warning from JSM about ignored events (could be removed once warnings are removed too)
        ...whileIn(State.SOURCE_CHANGING).passOver(Event.START_BUFFERING, Event.TIMECHANGED),
        // Once the source is loaded, we are READY to receive play interaction
        {name: Event.SOURCE_LOADED, from: State.SOURCE_CHANGING, to: State.READY},

        // Because of this state transition, we are able to male "a cut" in the current PLAYING / PAUSE state
        // and send "playing" / "pause" sample with the current custom data values. The new changed custom data values
        // will be set on upcoming samples after we transition back from the CUSTOMDATACHANGE state.
        // For any other states, we don't care, because InternalAdapter is not emitting the event.
        {name: Event.CUSTOM_DATA_CHANGE, from: [State.PLAYING, State.PAUSE], to: State.CUSTOMDATACHANGE},
        {name: Event.PLAYING, from: State.CUSTOMDATACHANGE, to: State.PLAYING},
        {name: Event.PAUSE, from: State.CUSTOMDATACHANGE, to: State.PAUSE},
      ],

      callbacks: {
        [`onleave${State.PLAYING}`]: () => {
          this.playingHeartbeatService.stopHeartbeat();
        },

        [`onleave${State.STARTUP}`]: (_event, _from, to, _eventTimestamp: number, _eventData) => {
          this.clearVideoStartTimeout();
          if (to === State.PLAYING) {
            this.startupHasSucceeded = true;
          }
        },

        onleavestate: (event, from, to, eventTimestamp: number, eventData: AnalyticsEventBase) => {
          logStateMachineTransition('LEAVE', event, from, to);

          // Skip this JSM callback for JSM initial event
          // This event is fired when the state machine is constructed,
          // to transition us from 'none' to custom initial 'SETUP' state
          if (event === JSM_INITIAL_EVENT) {
            return;
          }

          if (from === State.BUFFERING) {
            this.resetBufferingHelpers();
          }

          // Skip this JSM callback when an JSM event without a timestamp is called,
          // because it would cause NPE in analytics code (state duration calculation etc.).
          // This probably has happened because the state machine was not used correctly,
          // aka. JSM event was not called via `callEvent` method as it should be!
          if (!eventTimestamp) {
            return;
          }

          // Skip "default onleavestate analytics state machine callback" when leaving ERROR state,
          // because we call "error" state machine callback asap in "onafterEVENT" handler
          if (from === State.ERROR) {
            return;
          }

          // Skip, because we should ignore and do not track the "seeking" while the playback is paused
          // This means we should not use any callbacks, nor set any state properties
          if (
            (from === State.PAUSE && to === State.SEEKING_IN_PAUSE) ||
            (from === State.SEEKING_IN_PAUSE && to === State.PAUSE)
          ) {
            return;
          }

          // When leaving a state, before calling any callback (sending sample), we should set a correct sample `videoTimeEnd`
          if (eventData) {
            this.stateMachineCallbacks.setVideoTimeEndFromEvent(eventData);
          }

          const stateDuration = eventTimestamp - this.onEnterStateTimestamp;
          const lowerCaseFrom = String(from).toLowerCase();

          // Calling a custom callback when leaving STARTUP into STARTUP_FAILURE
          if (from === State.STARTUP && to === State.VIDEOSTART_FAILED) {
            this.stateMachineCallbacks.videoStartFailed(stateDuration, createVideoStartFailedReason(event));
            return;
          }

          // Calling a custom callback when leaving SEEKING (started in the PAUSE/PLAY state)
          if (from === State.SEEKING) {
            this.stateMachineCallbacks.seeking(stateDuration);
            return;
          }

          // Calling a custom callback handling UNLOAD event
          if (event === Event.UNLOAD) {
            if (from === State.PLAYING) {
              this.stateMachineCallbacks.closedPlaying(stateDuration);
            }
            return;
          }

          // Default "onleavestate" state machine callback
          //
          // Every time, when we leave a state, we should try to call "state" callback,
          // to be able to make "a cut" and send analytics sample (if necessary)
          const defaultStateMachineCallbackFn = this.stateMachineCallbacks[lowerCaseFrom];
          if (isFunction(defaultStateMachineCallbackFn)) {
            // a try-catch block to add more resilience against callback errors
            try {
              defaultStateMachineCallbackFn(stateDuration, lowerCaseFrom, eventData);
            } catch (e) {
              logger.error('Exception occurred in State Machine callback ' + lowerCaseFrom, eventData, e);
            }
          } else {
            // if callback is not defined, log a warning to indicate to the developer that
            //  - a new callback function needs to be implemented to relay on this default state machine callback
            //  - or if no callback is needed, we should
            //    - early return from this JSM callback
            //    - or add this state to the ignored list
            logMissingCallbackWarning(from, [State.READY, State.SOURCE_CHANGING]);
          }
        },

        [`onenter${State.PLAYING}`]: () => {
          this.playingHeartbeatService.startHeartbeat();
        },

        [`onenter${State.SOURCE_CHANGING}`]: (_event, _from, _to, _eventTimestamp, eventData) => {
          this.stateMachineCallbacks.manualSourceChange(eventData);
          this.startupHasSucceeded = false;
        },

        [`onenter${State.STARTUP}`]: () => {
          this.setVideoStartTimeout();
        },

        onenterstate: (event, from, to, eventTimestamp: number, eventData: AnalyticsEventBase) => {
          logStateMachineTransition('ENTER', event, from, to);

          // Skip, because we should ignore and do not track the "seeking" while the playback is paused
          // This means we should not use any callbacks, nor set any state properties
          if (
            (from === State.PAUSE && to === State.SEEKING_IN_PAUSE) ||
            (from === State.SEEKING_IN_PAUSE && to === State.PAUSE)
          ) {
            return;
          }

          this.onEnterStateTimestamp = eventTimestamp ?? Date.now();

          if (eventData) {
            this.stateMachineCallbacks.setVideoTimeStartFromEvent(eventData);
          }

          if (to === State.BUFFERING) {
            this.startBufferingHeartbeatInterval();
          }
        },

        [`onafter${Event.ERROR}`]: (_event, _from, _to, _eventTimestamp, eventData) => {
          this.stateMachineCallbacks.error(eventData);
        },

        [`onafter${Event.MANUAL_SOURCE_CHANGE}`]: (_event, from, to, _eventTimestamp, eventData) => {
          // To support the case: ADAPTER API SOURCE CHANGE VS. PLAYER SOURCE CHANGE
          // If the event was called while we are already in State.SOURCE_CHANGING, and we will stay there, it means:
          //  1) AdapterAPI.changeSource was called and now the "real" player source change is happening
          //  2) player source change was detected by InternalAdapter, and now the "later call" of AdapterAPI.changeSource
          //     is happening, so we should update the analytics config with the new one provided in the event data.
          if (from === State.SOURCE_CHANGING && to === State.SOURCE_CHANGING) {
            this.stateMachineCallbacks.manualSourceChange(eventData); // event data contains the new analytics config (if provided)
          }
        },
      },
    });
  }
}
